import { Component } from '@theme/component';
import { sectionRenderer } from '@theme/section-renderer';
import { requestIdleCallback, viewTransition } from '@theme/utilities';
import { ThemeEvents } from '@theme/events';
import { PaginatedListAspectRatioHelper } from '@theme/paginated-list-aspect-ratio';

const INFINITE_SCROLL_ROOT_MARGIN = '800px 0px 1200px 0px';
const INFINITE_LOADING_ATTRIBUTE = 'data-collection-infinite-loading';
const INFINITE_DEFER_ATTRIBUTE = 'data-infinite-defer-init';
const APPEND_CHUNK_SIZE = 4;
const INFINITE_SCROLL_PRELOAD_DISTANCE = 1200;
const INFINITE_SCROLL_TOP_PRELOAD_DISTANCE = 800;

const waitForNextFrame = () =>
  new Promise((resolve) => {
    requestAnimationFrame(() => {
      setTimeout(resolve, 0);
    });
  });

/**
 * A custom element that renders a paginated list of items.
 *
 * @typedef {object} Refs
 * @property {HTMLUListElement} [grid] - The grid element.
 * @property {HTMLSpanElement} [viewMorePrevious] - The view more previous button.
 * @property {HTMLSpanElement} [viewMoreNext] - The view more next button.
 * @property {HTMLElement[]} [cards] - The cards elements.
 * @property {HTMLDivElement} [viewMoreNextLoader] - The next page loader element.
 *
 * @extends Component<Refs>
 */
export default class PaginatedList extends Component {
  /**
   * @type {Map<number, string>}
   */
  pages = new Map();

  /** @type {IntersectionObserver | undefined} */
  infinityScrollObserver;

  /** @type {((value: void) => void) | null} */
  #resolveNextPagePromise = null;

  /** @type {((value: void) => void) | null} */
  #resolvePreviousPagePromise = null;

  /** @type {PaginatedListAspectRatioHelper} */
  #aspectRatioHelper;

  /** @type {boolean} */
  #isRenderingNextPage = false;

  /** @type {boolean} */
  #isRenderingPreviousPage = false;

  /** @type {Set<number>} */
  #renderedPages = new Set();

  /** @type {Set<number>} */
  #renderingPages = new Set();

  /** @type {Set<string>} */
  #renderedProductIds = new Set();

  /** @type {Map<number, Promise<void>>} */
  #fetchingPagePromises = new Map();

  /** @type {number} */
  #pageRequestGeneration = 0;

  /** @type {Element | null} */
  #observedPreviousTarget = null;

  /** @type {Element | null} */
  #observedNextTarget = null;

  /** @type {number | null} */
  #fallbackCheckFrame = null;

  connectedCallback() {
    super.connectedCallback();
    this.#syncRenderedPagesFromGrid();

    /** @type {HTMLElement | null} */
    const templateCard = this.querySelector('[ref="cardGallery"]');
    if (templateCard) {
      this.#aspectRatioHelper = new PaginatedListAspectRatioHelper({
        templateCard,
      });
    }

    this.#fetchPage('next');
    this.#fetchPage('previous');
    this.#observeViewMore();

    // Listen for filter updates to clear cached pages
    document.addEventListener(ThemeEvents.FilterUpdate, this.#handleFilterUpdate);
  }

  get shouldUpdatePageParamInUrl() {
    return this.getAttribute('update-url-on-scroll') !== 'false';
  }

  /**
   * Restores the collection state after navigating back from PDP.
   * @param {{ cardId?: string, page?: number } | null} state
   */
  async restoreFromStoredState(state) {
    if (!state) return;

    const targetPage = Number(state.page || 1);
    const targetCardId = state.cardId;

    await this.#ensurePagesLoaded(targetPage);

    if (typeof targetCardId === 'string' && targetCardId) {
      const targetCard = document.getElementById(targetCardId);
      if (targetCard) {
        targetCard.scrollIntoView({ behavior: 'instant', block: 'center' });
      }
    }

    requestIdleCallback(() => {
      this.#fetchPage('next');
      this.#fetchPage('previous');
    });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    if (this.infinityScrollObserver) {
      this.infinityScrollObserver.disconnect();
    }
    if (this.#fallbackCheckFrame !== null) {
      cancelAnimationFrame(this.#fallbackCheckFrame);
      this.#fallbackCheckFrame = null;
    }
    // Remove the filter update listener
    document.removeEventListener(ThemeEvents.FilterUpdate, this.#handleFilterUpdate);
  }

  #observeViewMore() {
    const { viewMorePrevious, viewMoreNext } = this.refs;

    // Return if neither element exists
    if (!viewMorePrevious && !viewMoreNext) return;

    // Create observer if it doesn't exist
    if (!this.infinityScrollObserver) {
      this.infinityScrollObserver = new IntersectionObserver(
        async (entries) => {
          // Wait for any in-progress view transitions to finish
          if (viewTransition.current) await viewTransition.current;

          for (const entry of entries) {
            if (entry.isIntersecting) {
              // Use current refs to check which element triggered
              const { viewMorePrevious, viewMoreNext } = this.refs;

              if (entry.target === viewMorePrevious) {
                this.#renderPreviousPage();
              } else if (entry.target === viewMoreNext) {
                this.#renderNextPage();
              }
            }
          }
        },
        {
          rootMargin: INFINITE_SCROLL_ROOT_MARGIN,
        }
      );
    }

    this.#resetObservedTarget('previous', viewMorePrevious);
    this.#resetObservedTarget('next', viewMoreNext);
  }

  /**
   * @param {"previous" | "next"} type
   * @param {Element | undefined} target
   */
  #resetObservedTarget(type, target) {
    if (!this.infinityScrollObserver) return;

    const observedTarget = type === 'next' ? this.#observedNextTarget : this.#observedPreviousTarget;
    if (observedTarget) {
      this.infinityScrollObserver.unobserve(observedTarget);
    }

    const nextTarget = target ?? null;
    if (nextTarget) {
      this.infinityScrollObserver.observe(nextTarget);
    }

    if (type === 'next') {
      this.#observedNextTarget = nextTarget;
    } else {
      this.#observedPreviousTarget = nextTarget;
    }
  }

  /**
   * @param {{ page: number, url?: URL } | undefined} pageInfo - The page info
   * @returns {boolean} Whether to use the page
   */
  #shouldUsePage(pageInfo) {
    if (!pageInfo) return false;

    const { grid } = this.refs;
    const lastPage = grid?.dataset.lastPage;

    if (!lastPage || pageInfo.page < 1 || pageInfo.page > Number(lastPage)) return false;

    return true;
  }

  /**
   * @param {"previous" | "next"} type
   */
  async #fetchPage(type) {
    const page = this.#getPage(type);

    // Always resolve the promise, even if we can't fetch the page
    const resolvePromise = () => {
      if (type === 'next') {
        this.#resolveNextPagePromise?.();
        this.#resolveNextPagePromise = null;
      } else {
        this.#resolvePreviousPagePromise?.();
        this.#resolvePreviousPagePromise = null;
      }
    };

    if (!page || !this.#shouldUsePage(page)) {
      // Resolve the promise even if we can't fetch
      resolvePromise();
      return;
    }

    try {
      await this.#fetchSpecificPage(page.page, page.url);
    } catch (error) {
      console.error(error);
    } finally {
      resolvePromise();
    }
  }

  /**
   * @param {number} pageNumber - The page number to fetch
   * @param {URL} [url] - Optional URL, will be constructed if not provided
   */
  async #fetchSpecificPage(pageNumber, url = undefined) {
    const pageInfo = { page: pageNumber, url };

    if (!url) {
      const newUrl = new URL(window.location.href);
      newUrl.searchParams.set('page', pageNumber.toString());
      newUrl.hash = '';
      pageInfo.url = newUrl;
    }

    if (!this.#shouldUsePage(pageInfo)) return;
    if (this.pages.has(pageNumber)) return;

    const pendingPage = this.#fetchingPagePromises.get(pageNumber);
    if (pendingPage) {
      await pendingPage;
      return;
    }

    const requestGeneration = this.#pageRequestGeneration;
    const pagePromise = sectionRenderer
      .getSectionHTML(this.sectionId, true, pageInfo.url)
      .then((pageContent) => {
        if (requestGeneration === this.#pageRequestGeneration) {
          this.pages.set(pageNumber, pageContent);
        }
      })
      .finally(() => {
        if (this.#fetchingPagePromises.get(pageNumber) === pagePromise) {
          this.#fetchingPagePromises.delete(pageNumber);
        }
      });

    this.#fetchingPagePromises.set(pageNumber, pagePromise);
    await pagePromise;
  }

  async #renderNextPage() {
    if (this.#isRenderingNextPage) return;
    this.#isRenderingNextPage = true;
    let renderingPage = null;

    try {
      const { grid } = this.refs;

      if (!grid) return;

      const nextPage = this.#getPage('next');

      if (!nextPage || !this.#shouldUsePage(nextPage)) {
        this.#setNextLoaderState(false);
        this.#removeNextLoaderElements();
        return;
      }

      if (this.#hasPageInGrid(nextPage.page) || this.#renderingPages.has(nextPage.page)) {
        requestIdleCallback(() => {
          this.#fetchPage('next');
        });
        return;
      }

      renderingPage = nextPage.page;
      this.#renderingPages.add(nextPage.page);
      const loaderStartTime = performance.now();
      this.#setNextLoaderState(true);

      let nextPageItemElements = this.#getGridForPage(nextPage.page);

      if (!nextPageItemElements) {
        const promise = new Promise((res) => {
          this.#resolveNextPagePromise = res;
        });

        // Trigger the fetch for this page
        this.#fetchPage('next');

        await promise;
        nextPageItemElements = this.#getGridForPage(nextPage.page);
        if (!nextPageItemElements) {
          this.#setNextLoaderState(false);
          return;
        }
      }

      await this.#ensureMinimumLoaderTime(loaderStartTime);

      const uniqueNextItems = this.#filterUniqueItems(nextPageItemElements);
      if (uniqueNextItems.length) {
        await this.#appendItems(grid, uniqueNextItems);
        this.#aspectRatioHelper.processNewElements();
        this.#observeViewMore();
      }

      this.#renderedPages.add(nextPage.page);
      this.#setNextLoaderState(false);

      if (this.shouldUpdatePageParamInUrl) {
        history.pushState('', '', nextPage.url.toString());
      }

      requestIdleCallback(() => {
        this.#fetchPage('next');
      });
    } finally {
      if (renderingPage != null) this.#renderingPages.delete(renderingPage);
      this.#setNextLoaderState(false);
      this.#isRenderingNextPage = false;
      this.#scheduleNextSentinelFallbackCheck();
    }
  }

  /**
   * @param {number} targetPage
   */
  async #ensurePagesLoaded(targetPage) {
    const { grid } = this.refs;
    if (!grid) return;

    const normalizedTargetPage = Math.floor(Number(targetPage));
    if (!Number.isFinite(normalizedTargetPage) || normalizedTargetPage <= 1) return;

    const lastPage = Number(grid.dataset.lastPage || 1);
    const maxTargetPage = Math.min(normalizedTargetPage, lastPage);

    const cards = grid.querySelectorAll(':scope > [ref="cards[]"]');
    const lastRenderedPage = Number(cards[cards.length - 1]?.getAttribute('data-page') || 1);

    for (let page = lastRenderedPage + 1; page <= maxTargetPage; page += 1) {
      const appended = await this.#appendPage(page);
      if (!appended) break;
    }
  }

  /**
   * @param {number} page
   * @returns {Promise<boolean>}
   */
  async #appendPage(page) {
    const { grid } = this.refs;
    if (!grid) return false;
    if (this.#hasPageInGrid(page) || this.#renderingPages.has(page)) return true;
    this.#renderingPages.add(page);

    try {
      let pageItems = this.#getGridForPage(page);

      if (!pageItems?.length) {
        const url = new URL(window.location.href);
        url.searchParams.set('page', page.toString());
        url.hash = '';

        await this.#fetchSpecificPage(page, url);
        pageItems = this.#getGridForPage(page);
      }

      if (!pageItems?.length) return false;

      const uniqueItems = this.#filterUniqueItems(pageItems);
      if (uniqueItems.length) {
        await this.#appendItems(grid, uniqueItems);
        this.#aspectRatioHelper.processNewElements();
      }

      this.#renderedPages.add(page);
      return true;
    } finally {
      this.#renderingPages.delete(page);
    }
  }

  async #renderPreviousPage() {
    if (this.#isRenderingPreviousPage) return;
    this.#isRenderingPreviousPage = true;
    let renderingPage = null;

    try {
      const { grid } = this.refs;

      if (!grid) return;

      const previousPage = this.#getPage('previous');
      if (!previousPage || !this.#shouldUsePage(previousPage)) return;

      if (this.#hasPageInGrid(previousPage.page) || this.#renderingPages.has(previousPage.page)) {
        requestIdleCallback(() => {
          this.#fetchPage('previous');
        });
        return;
      }

      renderingPage = previousPage.page;
      this.#renderingPages.add(previousPage.page);

      let previousPageItemElements = this.#getGridForPage(previousPage.page);
      if (!previousPageItemElements) {
        const promise = new Promise((res) => {
          this.#resolvePreviousPagePromise = res;
        });

        // Trigger the fetch for this page
        this.#fetchPage('previous');

        await promise;
        previousPageItemElements = this.#getGridForPage(previousPage.page);
        if (!previousPageItemElements) return;
      }

      // Store the current scroll position and height of the first element
      const scrollTop = window.scrollY;
      const firstElement = grid.firstElementChild;
      const oldHeight = firstElement ? firstElement.getBoundingClientRect().top + window.scrollY : 0;

      const uniquePreviousItems = this.#filterUniqueItems(previousPageItemElements);
      if (uniquePreviousItems.length) {
        // Prepend the new elements
        await this.#prependItems(grid, uniquePreviousItems);
        this.#aspectRatioHelper.processNewElements();
      }

      this.#renderedPages.add(previousPage.page);

      if (this.shouldUpdatePageParamInUrl) {
        history.pushState('', '', previousPage.url.toString());
      }

      // Calculate and adjust scroll position to maintain the same view
      if (firstElement) {
        const newHeight = firstElement.getBoundingClientRect().top + window.scrollY;
        const heightDiff = newHeight - oldHeight;
        window.scrollTo({
          top: scrollTop + heightDiff,
          behavior: 'instant',
        });
      }

      requestIdleCallback(() => {
        this.#fetchPage('previous');
      });
    } finally {
      if (renderingPage != null) this.#renderingPages.delete(renderingPage);
      this.#isRenderingPreviousPage = false;
    }
  }

  /**
   * @param {number} page
   * @returns {boolean}
   */
  #hasPageInGrid(page) {
    if (this.#renderedPages.has(page)) return true;
    const { grid } = this.refs;
    if (!grid) return false;
    return Boolean(grid.querySelector(`[ref="cards[]"][data-page="${page}"]`));
  }

  /**
   * @param {"previous" | "next"} type
   * @returns {{ page: number, url: URL } | undefined}
   */
  #getPage(type) {
    const { grid } = this.refs;
    const isPrevious = type === 'previous';
    const cards = grid?.querySelectorAll(':scope > [ref="cards[]"]');

    if (!cards?.length) return;

    const targetCard = cards[isPrevious ? 0 : cards.length - 1];

    if (!targetCard) return;

    const currentCardPage = Number(targetCard.dataset.page);
    const renderedPages = [...this.#renderedPages].filter((page) => Number.isFinite(page) && page > 0);

    let basePage = currentCardPage;
    if (isPrevious) {
      if (renderedPages.length > 0) {
        basePage = Math.min(currentCardPage, ...renderedPages);
      }
    } else if (renderedPages.length > 0) {
      basePage = Math.max(currentCardPage, ...renderedPages);
    }

    const page = isPrevious ? basePage - 1 : basePage + 1;

    const url = new URL(window.location.href);
    url.searchParams.set('page', page.toString());
    url.hash = '';

    return {
      page,
      url,
    };
  }

  /**
   * @param {number} page
   * @returns {NodeListOf<Element> | undefined}
   */
  #getGridForPage(page) {
    const pageHTML = this.pages.get(page);

    if (!pageHTML) return;

    const parsedPage = new DOMParser().parseFromString(pageHTML, 'text/html');
    const gridElement = parsedPage.querySelector('[ref="grid"]');
    if (!gridElement) return;
    return gridElement.querySelectorAll(':scope > [ref="cards[]"]');
  }

  get sectionId() {
    const id = this.getAttribute('section-id');

    if (!id) throw new Error('The section-id attribute is required');

    return id;
  }

  /**
   * Handle filter updates by clearing cached pages
   */
  #handleFilterUpdate = () => {
    this.#pageRequestGeneration += 1;
    this.pages.clear();
    this.#fetchingPagePromises.clear();
    this.#renderingPages.clear();
    this.#renderedPages.clear();
    this.#renderedProductIds.clear();
    this.#setNextLoaderState(false);

    // Resolve any pending promises to unblock waiting renders
    this.#resolveNextPagePromise?.();
    this.#resolvePreviousPagePromise?.();

    this.#resolveNextPagePromise = null;
    this.#resolvePreviousPagePromise = null;

    // Store the current lastPage value to detect when it changes
    const currentLastPage = this.refs.grid?.dataset.lastPage;

    // We need to wait for the DOM to be updated with the new filtered content
    // Using mutation observer to detect when the grid actually updates
    const observer = new MutationObserver(() => {
      // Check if data-last-page changed
      const newLastPage = this.refs.grid?.dataset.lastPage;

      if (newLastPage !== currentLastPage) {
        observer.disconnect();

        // Check if component is still connected
        if (!this.isConnected) {
          return;
        }

        // Now the DOM has been updated with the new filtered content
        this.#syncRenderedPagesFromGrid();
        this.#observeViewMore();

        // Fetch the next page
        this.#fetchPage('next');
      }
    });

    // Observe the grid for changes
    const { grid } = this.refs;
    if (grid) {
      observer.observe(grid, {
        attributes: true,
        attributeFilter: ['data-last-page'],
        childList: true, // Also watch for child changes in case the whole grid is replaced
      });

      // Set a timeout as a fallback in case the mutation never fires
      setTimeout(() => {
        if (observer) {
          observer.disconnect();
        }
      }, 3000);
    }
  };

  /**
   * @param {boolean} isLoading
   */
  #setNextLoaderState(isLoading) {
    const { viewMoreNextLoader } = this.refs;
    document.documentElement.toggleAttribute(INFINITE_LOADING_ATTRIBUTE, isLoading);

    if (!viewMoreNextLoader) return;

    viewMoreNextLoader.hidden = !isLoading;
    viewMoreNextLoader.setAttribute('aria-hidden', String(!isLoading));
  }

  #removeNextLoaderElements() {
    const loaderContainer = this.querySelector('[ref="viewMoreNextLoader"]');
    const viewMoreNext = this.querySelector('[ref="viewMoreNext"]');

    if (viewMoreNext && this.infinityScrollObserver) {
      this.infinityScrollObserver.unobserve(viewMoreNext);
    }
    if (this.#observedNextTarget === viewMoreNext) {
      this.#observedNextTarget = null;
    }

    loaderContainer?.remove();
    viewMoreNext?.remove();
  }

  #scheduleNextSentinelFallbackCheck() {
    if (this.#fallbackCheckFrame !== null) {
      cancelAnimationFrame(this.#fallbackCheckFrame);
    }

    this.#fallbackCheckFrame = requestAnimationFrame(() => {
      this.#fallbackCheckFrame = requestAnimationFrame(() => {
        this.#fallbackCheckFrame = null;
        this.#checkNextSentinelPosition();
      });
    });
  }

  #checkNextSentinelPosition() {
    if (!this.isConnected || this.#isRenderingNextPage) return;

    const { viewMoreNext } = this.refs;
    if (!(viewMoreNext instanceof Element)) return;

    const nextPage = this.#getPage('next');
    if (!nextPage || !this.#shouldUsePage(nextPage)) return;
    if (this.#hasPageInGrid(nextPage.page) || this.#renderingPages.has(nextPage.page)) return;

    const rect = viewMoreNext.getBoundingClientRect();
    const isWithinPreloadRange =
      rect.top <= window.innerHeight + INFINITE_SCROLL_PRELOAD_DISTANCE &&
      rect.bottom >= -INFINITE_SCROLL_TOP_PRELOAD_DISTANCE;

    if (isWithinPreloadRange) {
      this.#renderNextPage();
    }
  }

  /**
   * Keeps loader visible briefly so users can perceive loading feedback.
   * @param {number} startTime
   */
  async #ensureMinimumLoaderTime(startTime) {
    const minimumVisibleMs = 80;
    const elapsedMs = performance.now() - startTime;
    const remainingMs = minimumVisibleMs - elapsedMs;

    if (remainingMs <= 0) return;
    await new Promise((resolve) => setTimeout(resolve, remainingMs));
  }

  #syncRenderedPagesFromGrid() {
    this.#renderedPages.clear();
    this.#renderedProductIds.clear();
    const { grid } = this.refs;
    const cards = grid?.querySelectorAll(':scope > [ref="cards[]"]');
    if (!cards?.length) return;

    for (const card of cards) {
      const page = Number(card.dataset.page);
      if (Number.isFinite(page) && page > 0) {
        this.#renderedPages.add(page);
      }

      const productId = card.dataset.productId;
      if (productId) {
        this.#renderedProductIds.add(productId);
      }
    }
  }

  /**
   * @param {HTMLUListElement} grid
   * @param {Element[]} items
   */
  #appendItems(grid, items) {
    return this.#insertItemsInChunks(grid, items, 'append');
  }

  /**
   * @param {HTMLUListElement} grid
   * @param {Element[]} items
   */
  #prependItems(grid, items) {
    return this.#insertItemsInChunks(grid, items, 'prepend');
  }

  /**
   * @param {HTMLUListElement} grid
   * @param {Element[]} items
   * @param {"append" | "prepend"} position
   */
  async #insertItemsInChunks(grid, items, position) {
    const chunks = [];
    for (let index = 0; index < items.length; index += APPEND_CHUNK_SIZE) {
      chunks.push(items.slice(index, index + APPEND_CHUNK_SIZE));
    }

    for (const chunk of chunks) {
      for (const item of chunk) {
        item.setAttribute(INFINITE_DEFER_ATTRIBUTE, '');
      }

      const fragment = document.createDocumentFragment();
      for (const item of chunk) {
        fragment.appendChild(item);
      }
      const newNodes = [...fragment.children];

      if (position === 'prepend') {
        grid.prepend(fragment);
      } else {
        grid.appendChild(fragment);
      }

      await waitForNextFrame();
      this.#markAppendedNodesReady(newNodes);
    }

    requestIdleCallback(() => {
      for (const item of items) {
        item.removeAttribute(INFINITE_DEFER_ATTRIBUTE);
      }
    });
  }

  /**
   * @param {Element[]} nodes
   */
  #markAppendedNodesReady(nodes) {
    for (const node of nodes) {
      if (node instanceof HTMLElement) {
        node.removeAttribute(INFINITE_DEFER_ATTRIBUTE);
      }
    }
  }

  /**
   * @param {NodeListOf<Element> | Element[]} elements
   * @returns {Element[]}
   */
  #filterUniqueItems(elements) {
    const items = Array.from(elements);
    const seenProductIds = new Set();
    return items.filter((item) => {
      const productId = item.getAttribute('data-product-id');
      if (!productId) return true;
      if (seenProductIds.has(productId)) return false;
      if (this.#hasProductInGrid(productId)) return false;
      seenProductIds.add(productId);
      this.#renderedProductIds.add(productId);
      return true;
    });
  }

  /**
   * @param {string} productId
   * @returns {boolean}
   */
  #hasProductInGrid(productId) {
    return this.#renderedProductIds.has(productId);
  }
}
